'use server'

import { apiRequest, ApiResponse } from '../request'
import { Professional } from '../common/interfaces/professional.interface'

// Enums
export type TeleconsultationStatus =
    | 'CREATED'
    | 'WAITING_PATIENT'
    | 'WAITING_DOCTOR'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'EXPIRED'

export type TeleconsultationParticipantRole = 'PROFESSIONAL' | 'PATIENT'

// Interfaces
export interface TeleconsultationRoom {
    id: number
    dailyRoomName: string
    dailyRoomUrl: string
    dailyRoomId?: string
    status: TeleconsultationStatus
    scheduledAt: string
    startedAt?: string
    endedAt?: string
    duration?: number
    patientToken: string
    patientTokenExpiry?: string
    patientDailyToken?: string
    professionalDailyToken?: string
    recordingEnabled: boolean
    dailyRecordingId?: string
    recordingUrl?: string
    enableChat: boolean
    enableScreenShare: boolean
    maxDuration: number
    notes?: string
    title?: string
    accessLink?: string
    consultationId?: number
    agendaEventId?: number
    professionalId: number
    clientId: number
    companyId: number
    createdAt: string
    updatedAt: string
    createdById?: number
    // Relações
    professional: Professional
    createdBy?: {
        id: number
        name: string
        email?: string
    }
    client?: {
        id: number
        name: string
        cpf?: string
        email?: string
        whatsAppNumber?: string
    }
    company?: { id: number; name: string }
    consultation?: { id: number; status: string; isReturn?: boolean; specialtyId?: number; price?: number; sellingPrice?: number; markupPercentage?: number; discount?: number; finalPrice?: number; partnerId?: number }
    billingId?: number
    billing?: {
        id: number
        status: string
        amount: number
        paidAmount?: number | null
        paymentMethod?: string | null
        dueDate: string
        paidAt?: string | null
        publicToken?: string | null
        description?: string | null
        pixQrCodeBase64?: string | null
        pixCopyPaste?: string | null
        bankSlipUrl?: string | null
        barcode?: string | null
        digitableLine?: string | null
        paymentLink?: string | null
    }
    specialtyId?: number
    specialty?: { id: number; name: string }
    price?: number
    sellingPrice?: number
    markupPercentage?: number
    discount?: number
    finalPrice?: number
    dealId?: number
    deal?: {
        id: number
        title: string
        status: string
        totalValue?: number
        stage: { id: number; name: string; color?: string }
        pipeline: { id: number; name: string; type: string }
        lead: { id: number; name: string; phoneNumber?: string; email?: string }
    }
    participants?: TeleconsultationParticipant[]
    chatMessages?: TeleconsultationChatMessage[]
}

export interface TeleconsultationParticipant {
    id: number
    teleconsultationRoomId: number
    role: TeleconsultationParticipantRole
    name: string
    userId?: number
    clientId?: number
    joinedAt: string
    leftAt?: string
    duration?: number
}

export interface TeleconsultationChatMessage {
    id: number
    teleconsultationRoomId: number
    senderRole: TeleconsultationParticipantRole
    senderName: string
    message: string
    createdAt: string
}

export interface CreateTeleconsultationData {
    clientId: number
    professionalId: number
    companyId: number
    scheduledAt: string
    maxDuration?: number
    recordingEnabled?: boolean
    enableChat?: boolean
    enableScreenShare?: boolean
    notes?: string
    title?: string
    isReturn?: boolean
    specialtyId?: number
    price?: number
    sellingPrice?: number
    markupPercentage?: number
    discount?: number
    paymentMethod?: string
    /** Parcelas no cartão combinadas com o pagador (trava o checkout). */
    installmentCount?: number
    partnerId?: number
    healthPlanId?: number
    pipelineId?: number
}

export interface UpdateTeleconsultationData {
    scheduledAt?: string
    maxDuration?: number
    recordingEnabled?: boolean
    enableChat?: boolean
    enableScreenShare?: boolean
    notes?: string
    specialtyId?: number
    price?: number
    sellingPrice?: number
    markupPercentage?: number
    discount?: number
    paymentMethod?: string
    /** Parcelas no cartão combinadas com o pagador (trava o checkout). */
    installmentCount?: number
}

// CRUD
export async function getTeleconsultations(
    params?: {
        page?: number
        limit?: number
        search?: string
        status?: TeleconsultationStatus
        professionalId?: number
        startDate?: string
        endDate?: string
        id?: number
        cpf?: string
        partnerId?: number
    }
): Promise<ApiResponse<TeleconsultationRoom[]>> {
    return apiRequest<TeleconsultationRoom[]>('teleconsultations', { params: params || {} })
}

export async function getTeleconsultation(
    id: number
): Promise<ApiResponse<TeleconsultationRoom>> {
    return apiRequest<TeleconsultationRoom>(`teleconsultations/${id}`, {})
}

export async function createTeleconsultation(
    data: CreateTeleconsultationData
): Promise<ApiResponse<TeleconsultationRoom>> {
    return apiRequest<TeleconsultationRoom>(
        'teleconsultations',
        { data },
        'post'
    )
}

export async function updateTeleconsultation(
    id: number,
    data: UpdateTeleconsultationData
): Promise<ApiResponse<TeleconsultationRoom>> {
    return apiRequest<TeleconsultationRoom>(
        `teleconsultations/${id}`,
        { data },
        'patch'
    )
}

export async function cancelTeleconsultation(
    id: number
): Promise<ApiResponse<TeleconsultationRoom>> {
    return apiRequest<TeleconsultationRoom>(
        `teleconsultations/${id}`,
        {},
        'delete'
    )
}

// Ações
export async function startTeleconsultation(
    id: number
): Promise<ApiResponse<TeleconsultationRoom>> {
    return apiRequest<TeleconsultationRoom>(
        `teleconsultations/${id}/start`,
        {},
        'post'
    )
}

export async function rejoinTeleconsultation(
    id: number
): Promise<ApiResponse<TeleconsultationRoom>> {
    return apiRequest<TeleconsultationRoom>(
        `teleconsultations/${id}/rejoin`,
        {},
        'post'
    )
}

export async function endTeleconsultation(
    id: number
): Promise<ApiResponse<TeleconsultationRoom>> {
    return apiRequest<TeleconsultationRoom>(
        `teleconsultations/${id}/end`,
        {},
        'post'
    )
}

export async function resendTeleconsultationLink(
    id: number
): Promise<ApiResponse<{ message: string; accessLink: string }>> {
    return apiRequest<{ message: string; accessLink: string }>(
        `teleconsultations/${id}/resend-link`,
        {},
        'post'
    )
}

// Chat
export async function getTeleconsultationChat(
    id: number
): Promise<ApiResponse<TeleconsultationChatMessage[]>> {
    return apiRequest<TeleconsultationChatMessage[]>(
        `teleconsultations/${id}/chat`,
        {}
    )
}

export async function sendTeleconsultationChat(
    id: number,
    message: string
): Promise<ApiResponse<TeleconsultationChatMessage>> {
    return apiRequest<TeleconsultationChatMessage>(
        `teleconsultations/${id}/chat`,
        { data: { message } },
        'post'
    )
}

// Gravação
export interface TeleconsultationRecordingInfo {
    teleconsultationId: number
    recordingEnabled: boolean
    hasRecording: boolean
    recordingUrl?: string
    recordingDuration?: number
    recordingSize?: number
    consultationId?: number
    dailyRecordingId?: string
    message?: string
}

export async function getRecordingInfo(
    id: number
): Promise<ApiResponse<TeleconsultationRecordingInfo>> {
    return apiRequest<TeleconsultationRecordingInfo>(
        `teleconsultations/${id}/recording`,
        {}
    )
}

// Billing
export async function markTeleconsultationBillingAsPaid(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`teleconsultations/${id}/billing/mark-paid`, {}, 'patch')
}

export async function cancelTeleconsultationBilling(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`teleconsultations/${id}/billing/cancel`, {}, 'patch')
}

// Labels e cores para a UI
export const TELECONSULTATION_STATUS_LABELS: Record<
    TeleconsultationStatus,
    string
> = {
    CREATED: 'Criada',
    WAITING_PATIENT: 'Aguardando Paciente',
    WAITING_DOCTOR: 'Aguardando Profissional',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluída',
    CANCELLED: 'Cancelada',
    EXPIRED: 'Expirada',
}

export const TELECONSULTATION_STATUS_COLORS: Record<
    TeleconsultationStatus,
    string
> = {
    CREATED: 'badge-info',
    WAITING_PATIENT: 'badge-warning',
    WAITING_DOCTOR: 'badge-warning',
    IN_PROGRESS: 'badge-secondary',
    COMPLETED: 'badge-success',
    CANCELLED: 'badge-error',
    EXPIRED: 'badge-ghost',
}
